import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, Option, Select, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'src/mtska-frontend-app-component/assets';
import { Button } from '../Button';
import { Text } from '../Typography';
import SaveNamedColumnViewControls from './SaveNamedColumnViewControls';
import {
    handleApplyRecipe,
    handleApplySavedColumnView,
    isSavedColumnViewOptionValue,
    snapshotColumnStateMatchesGrid,
} from './list.functions';

const RecipeSelector = ({
    recipes,
    customRecipes = [],
    columnOrder,
    clearPageFiltersOnRecipe,
    apiRef,
    columns = [],
    visibilityModel,
    setSettingsKey,
    isManaged,
}) => {
    const { t } = useTranslation();
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);

    const closeSaveDialog = useCallback(() => {
        setSaveDialogOpen(false);
    }, []);

    useEffect(() => {
        if (!saveDialogOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeSaveDialog();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [saveDialogOpen, closeSaveDialog]);

    const options = useMemo(() => {
        const fromConfig = [];
        if (recipes) {
            if (Array.isArray(recipes)) {
                recipes.forEach((recipe, index) => {
                    if (Array.isArray(recipe)) {
                        fromConfig.push({
                            key: `recipe_${index + 1}`,
                            label: `${t('Recipe')} ${index + 1}`,
                            value: recipe,
                        });
                        return;
                    }
                    if (recipe && Array.isArray(recipe.columns)) {
                        fromConfig.push({
                            key: recipe.key || recipe.name || `recipe_${index + 1}`,
                            label: recipe.label || recipe.name || `${t('Recipe')} ${index + 1}`,
                            value: recipe.columns,
                        });
                    }
                });
            } else {
                Object.entries(recipes).forEach(([key, value]) => {
                    fromConfig.push({
                        key,
                        label: t(key),
                        value: Array.isArray(value) ? value : [],
                    });
                });
            }
        }

        const fromCustom = (Array.isArray(customRecipes) ? customRecipes : []).map((r) => ({
            key: `customRecipe:${r.id}`,
            label: r.label,
            value: {
                __savedColumnView: true,
                columnOrder: r.columnOrder,
                visibilityModel: r.visibilityModel,
            },
        }));

        return [...fromConfig, ...fromCustom];
    }, [recipes, customRecipes, t]);

    const activeRecipeKey = useMemo(() => {
        if (!visibilityModel || options.length === 0) return null;

        const comparableColumns = (Array.isArray(columns) ? columns : []).filter(
            (column) => column?.field && !column.field.startsWith('__') && column.hideable !== false,
        );

        if (comparableColumns.length === 0) return null;

        const comparableFieldSet = new Set(comparableColumns.map((column) => column.field));

        const active = options.find((option) => {
            if (isSavedColumnViewOptionValue(option.value)) {
                return snapshotColumnStateMatchesGrid(
                    option.value,
                    columnOrder,
                    visibilityModel,
                    columns,
                );
            }
            const recipeFieldSet = new Set(
                (Array.isArray(option.value) ? option.value : []).filter((field) =>
                    comparableFieldSet.has(field),
                ),
            );
            return comparableColumns.every((column) => {
                const isVisible = visibilityModel[column.field] !== false;
                return recipeFieldSet.has(column.field) ? isVisible : !isVisible;
            });
        });

        return active?.key ?? null;
    }, [columns, options, visibilityModel, columnOrder]);

    const layoutMatchesKnownView = activeRecipeKey != null;
    const saveIconDisabled = !apiRef?.current || layoutMatchesKnownView;
    const saveIconTitle = layoutMatchesKnownView
        ? t('save_column_view_disabled_known_layout')
        : t('save_column_view');

    if (options.length === 0) return null;

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    minWidth: { xs: 'min(100%, 200px)', sm: 220 },
                    maxWidth: { sm: 360 },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                    <Select
                        size="sm"
                        value={activeRecipeKey}
                        onChange={(e, recipeKey) => {
                            const selectedRecipe = options.find((option) => option.key === recipeKey);
                            const val = selectedRecipe?.value;
                            if (isSavedColumnViewOptionValue(val)) {
                                handleApplySavedColumnView(val, apiRef, setSettingsKey, clearPageFiltersOnRecipe);
                            } else {
                                handleApplyRecipe(val, apiRef, setSettingsKey, clearPageFiltersOnRecipe);
                            }
                        }}
                        variant="outlined"
                        autoComplete="off"
                        placeholder={t('Select a view')}
                        className="form-element-field form-element-field-select"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            bgcolor: 'background.surface',
                            '&:hover': { bgcolor: 'background.surface' },
                        }}
                    >
                        {options.map((option) => (
                            <Option key={option.key} value={option.key}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                    {!isManaged && (
                        <span title={saveIconTitle}>
                            <IconButton
                                family="fas"
                                icon="faSave"
                                disabled={saveIconDisabled}
                                style={{
                                    flexShrink: 0,
                                    color: 'var(--app-main)',
                                }}
                                onClick={() => setSaveDialogOpen(true)}
                            />
                        </span>
                    )}
                </Box>
            </Box>

            {saveDialogOpen &&
                !isManaged &&
                typeof document !== 'undefined' &&
                createPortal(
                    <Box
                        className="dialog dialog-confirm"
                        sx={{ position: 'relative', zIndex: 2000 }}
                    >
                        <Box className="dialog-backdrop" onClick={closeSaveDialog} role="presentation" />
                        <Box className="dialog-content">
                            <Text level="h3">{t('save_column_view_section')}</Text>
                            <Text level="body-md" style={{ marginBottom: '8px' }}>
                                {t('save_column_view_hint')}
                            </Text>
                            <SaveNamedColumnViewControls
                                layout="compact"
                                disabled={!apiRef?.current}
                                columnOrder={columnOrder}
                                visibilityModel={visibilityModel}
                                tableViewCustomRecipes={customRecipes}
                                setSettingsKey={setSettingsKey}
                                onSaveSuccess={closeSaveDialog}
                                sx={{
                                    mt: 0,
                                    pt: 0,
                                    borderTop: 'none',
                                    p: 0,
                                    mb: 2,
                                }}
                            />
                            <Box className="dialog-actions">
                                <Button variant="soft" className="dialog-ko" onClick={closeSaveDialog}>
                                    {t('Cancel')}
                                </Button>
                            </Box>
                        </Box>
                    </Box>,
                    document.body,
                )}
        </>
    );
};

export default RecipeSelector;
